// Launcher copy-token vocabulary — the behavior contract shared by Win Tool
// (`mini-apps/launcher`) and Playbooks (`mini-apps/playbooks`): outreach
// channels, sender tokens, the merge-token field list, each token's declared
// empty-value fallback, and the ONE resolver both the preview and the send
// path go through. Promoted here (mini-apps/CLAUDE.md, "Promotion rules") when
// Playbooks was extracted from Launcher V2 so both apps resolve `{{Tokens}}`
// against exactly one source of truth.
//
// ── THE THREE RULES (Playbooks send PRD U1 + the 2026-09-16 Clay-owned flip) ─
// A KNOWN token with an empty value is a DATA GAP: the token's declared
// `emptyFallback` fires and the send proceeds. A token outside the vocabulary
// is a COPY DEFECT: `resolveCopy` returns `ok: false`, leaves the literal in
// `text` so a caller can show the rep exactly what is wrong, and the caller
// decides. A CLAY-OWNED token (see {@link CLAY_OWNED_TOKENS}) is neither: it
// is DEFERRED — left literally in the text on purpose, reported on
// `ResolvedCopy.deferred`, and filled inside the Clay workflow.
// `resolveCopy` never throws for any of the three — they are ordinary copy
// states, not exceptions.
//
// ── WHY A DELIBERATE HANDOFF NOW EXISTS ──────────────────────────────────────
// Until 2026-09-16 there was no pass-through and the header said there could
// not be one, because Gong truncates a Terra-spelled token at the first space
// (verified live 2026-09-15: a literal `{{First Name}}` renders in the
// composer as `{{First}}`, in red). That reasoning was sound about GONG and
// is unchanged — what changed is that nothing Terra emits reaches Gong
// directly any more. Clay's V3 workflow gained a deterministic `Render copy`
// node that substitutes exactly {@link CLAY_OWNED_TOKENS} across all 20 steps
// AFTER sender resolution and company-name standardization, and HARD-STOPS
// the send if any brace survives. So the three tokens whose correct value is
// only knowable inside that workflow are handed over instead of guessed at.
//
// This is NOT the old `CLAY_RESOLVED_VARS` / `LEGACY_CLAY_PASSTHROUGH` set
// (deleted in U1). That one silently passed FOUR tokens through with no
// downstream guarantee at all — no node filled them, no node checked them, and
// `{{First Name}}` was among them. See {@link CLAY_OWNED_TOKENS}.
//
// Frontend-safe: no zod, no core imports.
import type { CampaignMember } from './launcher-campaign.types';
import {
  COPY_DEFECT_ERROR_PREFIX,
  SENDER_MISSING_ERROR_PREFIX,
} from '../../src/shared/launcher-dispatch-error-prefixes';

/**
 * The sender picker's vocabulary — the ONE list the Playbooks dropdown, the
 * label map (`sender-choice.ts#senderLabel`), the token/static classifier
 * (`copy-token-flags.ts#toSenderValue`) and {@link SenderValue}'s own union
 * are all derived from. Add a token here and every one of them follows.
 *
 * `met_with_email` (QA round 8, item 7 — 2026-09-22) is the SF campaign
 * member's scanner, synced to `primary_met_with_name/_first_name/_email` by
 * `src/services/launcher-member-sync.ts`. It is TERRA-ONLY: on the wire it
 * flips to Clay's `manual_email` (see `send-payload.ts#senderTokenOf`), so no
 * Clay workflow change is needed to add it. Launcher V1 keeps a deliberately
 * FROZEN copy of this list (`mini-apps/launcher/copy-tokens.ts`, pinned by
 * `launcher/__tests__/copy-tokens-pin.test.ts`) because its live V2 workflow
 * only knows the three owner tokens and would fall back to the account owner
 * for an unknown one — do NOT add the scanner there.
 */
export const SENDER_TOKENS = [
  { value: 'gtme_owner_email', label: 'GTME email' },
  { value: 'gs_owner_email',   label: 'GS email' },
  { value: 'claydr_email',     label: 'ClayDR email' },
  { value: 'met_with_email',   label: 'Primary met with (scanner)' },
] as const satisfies readonly { value: string; label: string }[];

/** Every legal `SenderValue.token`, DERIVED from {@link SENDER_TOKENS} so the two cannot drift. */
export type SenderTokenValue = (typeof SENDER_TOKENS)[number]['value'];

export interface SenderValue {
  type: 'token' | 'static';
  token?: SenderTokenValue;
  email?: string;
}

export type Channel = 'gong_engage' | 'inflection' | 'slack_copilot';

export const CHANNELS: { value: Channel; label: string }[] = [
  { value: 'gong_engage',   label: 'Gong Engage' },
  { value: 'inflection',    label: 'Inflection' },
  { value: 'slack_copilot', label: 'Terra Co-Pilot' },
];

// ── The Clay-owned handoff set ───────────────────────────────────────────────

/**
 * THE CONTRACT: these three tokens leave Terra as LITERAL `{{…}}` text. Clay's
 * deterministic `Render copy` node substitutes exactly this set (plus
 * `{{First Name}}` if it finds one) across all 20 steps after sender
 * resolution and company-name standardization, and HARD-STOPS the send if any
 * brace survives. Terra's job for them is to hand them over intact, and to say
 * so out loud on `ResolvedCopy.deferred` / the payload's `deferred_tokens` so
 * a deliberate handoff is distinguishable from a failure.
 *
 * Why each one, settled with the product owner on 2026-09-16 — in every case
 * because the correct value is only KNOWABLE INSIDE the Clay workflow, after
 * routing:
 *
 * - `Sender First Name` and `Sender Meeting Link` — Clay's "Resolve sender"
 *   step may pick a different sender than Terra's best guess (ClayDR
 *   round-robin for an unassigned contact is the standing example). Terra
 *   still SENDS its own guess as `sender_first_name`/`sender_meeting_link`,
 *   which Clay takes as one input to that chain; what it must not do is bake
 *   the guess into the copy.
 * - `Company Name` — Clay's standardizer owns the clean company name.
 *   `{{Account Name}}` (Salesforce's raw account name, which Terra DOES own)
 *   stays Terra-resolved and is the token to reach for when the rep wants
 *   Terra's value.
 *
 * `{{First Name}}` IS DELIBERATELY ABSENT. Terra resolves it, including its
 * declared `substitute: 'there'` fallback, and there is a test pinning that.
 * The ONE exception is the send path, which opts in per call with
 * {@link ResolveCopyOptions.deferToClay} = {@link CLAY_RENDERED_AT_SEND_TOKENS}
 * (Clay's Render copy fills the cleaned name) — every other surface still
 * shows the real name.
 *
 * ── THIS IS NOT `CLAY_RESOLVED_VARS` ────────────────────────────────────────
 * The name is different on purpose. The deleted `CLAY_RESOLVED_VARS` /
 * `LEGACY_CLAY_PASSTHROUGH` set (removed in U1) held FOUR tokens —
 * `First Name` among them — and passed them through with NO downstream
 * guarantee whatsoever: nothing in the V2 workflow filled them and nothing
 * checked them, so the literals went to Gong and rendered truncated, in red.
 * The difference now is not the size of the set, it is that a named node on
 * the other side is contractually responsible for every member of it and
 * refuses to send if it fails. Shrinking the old set would have hidden that;
 * a differently-named constant makes the two impossible to confuse in a grep.
 *
 * Adding a token here is a CROSS-SYSTEM change: the Clay `Render copy` node
 * has to learn it in the same cutover, or that token reaches Gong as literal
 * braces.
 */
export const CLAY_OWNED_TOKENS = [
  'Sender First Name',
  'Sender Meeting Link',
  'Company Name',
] as const;

export type ClayOwnedToken = (typeof CLAY_OWNED_TOKENS)[number];

/**
 * Lowercased lookup for the allowlist. Matching is by TRIMMED key,
 * CASE-INSENSITIVELY, because the allowlist guards a SEND: a rep who typed
 * `{{company name}}` meant the Clay-owned token, and classifying that as an
 * unknown token would skip the member for something Clay will fill correctly.
 * The narrowness that matters is the other direction — a typo
 * (`{{Sender Frist Name}}`) must NOT match, which is why this is an explicit
 * three-entry allowlist and not a pattern.
 */
const CLAY_OWNED_BY_LOWERCASE: ReadonlyMap<string, ClayOwnedToken> = new Map(
  CLAY_OWNED_TOKENS.map((token) => [token.toLowerCase(), token] as const),
);

/**
 * The DECLARED spelling of `key` when it names a Clay-owned token, else `null`.
 *
 * Callers canonicalize rather than echo the rep's spelling, so the literal
 * that leaves Terra is byte-exactly what Clay's `Render copy` node matches on:
 * `{{ company name }}` in the editor becomes `{{Company Name}}` on the wire.
 */
export function clayOwnedTokenOf(key: string): ClayOwnedToken | null {
  return CLAY_OWNED_BY_LOWERCASE.get(key.trim().toLowerCase()) ?? null;
}

/** Whether `key` names a Clay-owned token — see {@link clayOwnedTokenOf}. */
export function isClayOwnedToken(key: string): boolean {
  return CLAY_OWNED_BY_LOWERCASE.has(key.trim().toLowerCase());
}

/**
 * Tokens Terra leaves LITERAL on the SEND path only — Clay's published Render
 * copy fills them (V3 v10: Inflection "Recipient First Name" from the
 * standardizer's cleaned name, falling back to "there").
 *
 * NOT part of {@link CLAY_OWNED_TOKENS}, on purpose. That set is deferred on
 * EVERY surface (token picker, badges, editor/review previews), and a rep must
 * still see the real sample name in those. This set is passed per call as
 * {@link ResolveCopyOptions.deferToClay} by the three send-path builders and
 * by nothing else, so every other caller of the resolver is byte-for-byte
 * unchanged — Launcher included (it has its own frozen copy of the token list
 * and never reads this one).
 *
 * Adding a token here is a CROSS-SYSTEM change: Clay's `Render copy` node must
 * substitute it in the same cutover, or the literal reaches Clay's brace
 * hard-stop (a proven-safe refusal, never a brace to Gong).
 */
export const CLAY_RENDERED_AT_SEND_TOKENS = ['First Name'] as const;

/**
 * The declared spelling of `key` when it is Clay-owned OR named by the caller's
 * `deferToClay` list (trimmed, case-insensitive), else `null`. The one lookup
 * behind every deferral decision, so the resolver, the classifier and the two
 * residual scans cannot disagree about what is deferred.
 */
function deferredCanonicalOf(key: string, deferToClay: readonly string[] = []): string | null {
  const owned = clayOwnedTokenOf(key);
  if (owned !== null) return owned;
  const lower = key.trim().toLowerCase();
  return deferToClay.find((label) => label.toLowerCase() === lower) ?? null;
}

/**
 * The rep-facing caption on every surface that shows a Clay-owned token: the
 * picker row's value cell, the preview chip's tooltip, the resolution report's
 * bucket. One exported string rather than three literals, so the wording is
 * changed in one place and the tests assert against the same constant the UI
 * renders.
 */
export const CLAY_OWNED_TOKEN_CAPTION = 'Final value set by Clay at send';

/** The short form, for a dense cell (a picker row's right-hand value). */
export const CLAY_OWNED_TOKEN_SHORT_CAPTION = 'Set by Clay at send';

/**
 * What {@link resolveCopy} does with a token that IS in the vocabulary but has
 * no value for this member — declared once per token on
 * {@link COPY_VARIABLE_FIELDS} and applied by the resolver, so no call site
 * ever decides (Playbooks send PRD U1, R3; the `?? 'there'` scattered across
 * call sites is the exact thing that rule forbids).
 *
 * - `substitute` — put `text` where the token was. `text: ''` is a legal,
 *   deliberate declaration meaning "drop the token, keep the line" (that is
 *   `Last Name`: "Hi Jamie Rivera" degrades to "Hi Jamie", not to no greeting).
 * - `drop-line` — remove the whole enclosing line. A sign-off with no name, a
 *   CTA with no link and a fact sentence with no fact all read worse
 *   half-written than absent. What "the line" is depends on the copy: in plain
 *   text it is the newline-delimited line; in HTML it is the innermost
 *   VERIFIED block (or that block's `<br>` segment), and when no block can be
 *   verified it is JUST THE TOKEN — the newline rule never runs inside markup,
 *   because a serialized document is often one physical line (U3). See
 *   {@link applyDropLines}.
 *
 * An empty value is a DATA GAP, never a copy defect: whichever fallback fires,
 * the send proceeds. A token outside the vocabulary is the other rule — a copy
 * defect, reported through `ResolvedCopy.ok === false`.
 */
export type EmptyFallback =
  | { kind: 'substitute'; text: string }
  | { kind: 'drop-line' };

export interface CopyVariableField {
  label: string;
  variable: string;
  object: string;
  /**
   * `null` on a {@link CLAY_OWNED_TOKENS} row, and ONLY there — a Clay-owned
   * token never falls back in Terra, because Terra never substitutes it at
   * all. It is not "no policy declared yet": it is the declaration that the
   * empty-value question does not arise on this side of the handoff, which is
   * why the field is nullable rather than optional (an omitted key and a
   * deliberate `null` would read the same, and the first is drift).
   *
   * `EMPTY_FALLBACK_BY_LABEL` therefore has no entry for these rows, and
   * `classifyToken` can never reach the `empty` branch for one — it answers
   * `deferred` first.
   */
  emptyFallback: EmptyFallback | null;
}

/** Shorthand so the table below stays one readable row per token. */
const SUBSTITUTE = (text: string): EmptyFallback => ({ kind: 'substitute', text });
const DROP_LINE: EmptyFallback = { kind: 'drop-line' };
/** A {@link CLAY_OWNED_TOKENS} row — see {@link CopyVariableField.emptyFallback}. */
const CLAY_OWNED: null = null;

/**
 * The merge-token vocabulary. Every label here MUST be a key
 * `buildTemplateVars` emits, EXCEPT a {@link CLAY_OWNED_TOKENS} row that Terra
 * has no guess for at all (`Company Name`) — and every key
 * `buildTemplateVars` emits must have a row here. `resolveCopy`'s fallback
 * lookup is total only under that invariant, which
 * `__tests__/launcher-copy-tokens-fallback.test.ts` pins in both directions
 * with the Clay-owned exception written out explicitly.
 *
 * `Company Name` IS A REP-FACING TOKEN AGAIN (2026-09-16). It used to be
 * deliberately absent, on the reasoning that Clay's standardizer owns company
 * name and Terra never resolves it — so a `{{Company Name}}` in Terra copy was
 * an unknown token, a copy defect, exactly like a typo (R9). Clay's V3
 * `Render copy` node changed the second half of that: Clay still owns the
 * value, but now fills it deterministically after standardization and blocks
 * the send if it cannot. So the token belongs in the picker (a rep asking for
 * the standardized company name has a supported way to say it) with `null`
 * fallback and no `buildTemplateVars` key, because Terra genuinely has no
 * value for it. `{{Account Name}}` remains the Terra-owned, Terra-resolved
 * Salesforce account name.
 */
export const COPY_VARIABLE_FIELDS: CopyVariableField[] = [
  // Contact
  { object: 'Contact', label: 'First Name',       variable: '{{First Name}}',       emptyFallback: SUBSTITUTE('there') },
  { object: 'Contact', label: 'Last Name',        variable: '{{Last Name}}',        emptyFallback: SUBSTITUTE('') },
  { object: 'Contact', label: 'Full Name',        variable: '{{Full Name}}',        emptyFallback: SUBSTITUTE('there') },
  { object: 'Contact', label: 'Email',            variable: '{{Email}}',            emptyFallback: DROP_LINE },
  { object: 'Contact', label: 'Title',            variable: '{{Title}}',            emptyFallback: DROP_LINE },
  { object: 'Contact', label: 'Campaign Status',  variable: '{{Campaign Status}}',  emptyFallback: DROP_LINE },
  { object: 'Contact', label: 'Owner Role',       variable: '{{Owner Role}}',       emptyFallback: DROP_LINE },
  { object: 'Contact', label: 'Fit Score',        variable: '{{Fit Score}}',        emptyFallback: DROP_LINE },
  // Account
  { object: 'Account', label: 'Account Name',        variable: '{{Account Name}}',        emptyFallback: SUBSTITUTE('your team') },
  // Clay-owned: no `buildTemplateVars` key, no fallback, left literal for
  // Clay's standardizer. See `CLAY_OWNED_TOKENS`.
  { object: 'Account', label: 'Company Name',        variable: '{{Company Name}}',        emptyFallback: CLAY_OWNED },
  { object: 'Account', label: 'Sales Tier',           variable: '{{Sales Tier}}',          emptyFallback: DROP_LINE },
  { object: 'Account', label: 'GTME Owner Name',       variable: '{{GTME Owner Name}}',       emptyFallback: DROP_LINE },
  { object: 'Account', label: 'GTME Owner First Name', variable: '{{GTME Owner First Name}}', emptyFallback: DROP_LINE },
  { object: 'Account', label: 'GTME Owner Email',     variable: '{{GTME Owner Email}}',     emptyFallback: DROP_LINE },
  { object: 'Account', label: 'GTME Meeting Link',    variable: '{{GTME Meeting Link}}',    emptyFallback: DROP_LINE },
  { object: 'Account', label: 'GS Owner Name',        variable: '{{GS Owner Name}}',        emptyFallback: DROP_LINE },
  { object: 'Account', label: 'GS Owner First Name',  variable: '{{GS Owner First Name}}',  emptyFallback: DROP_LINE },
  { object: 'Account', label: 'GS Owner Email',       variable: '{{GS Owner Email}}',       emptyFallback: DROP_LINE },
  { object: 'Account', label: 'GS Meeting Link',      variable: '{{GS Meeting Link}}',      emptyFallback: DROP_LINE },
  { object: 'Account', label: 'ClayDR Name',          variable: '{{ClayDR Name}}',          emptyFallback: DROP_LINE },
  { object: 'Account', label: 'ClayDR First Name',    variable: '{{ClayDR First Name}}',    emptyFallback: DROP_LINE },
  { object: 'Account', label: 'ClayDR Email',         variable: '{{ClayDR Email}}',         emptyFallback: DROP_LINE },
  { object: 'Account', label: 'ClayDR Meeting Link',  variable: '{{ClayDR Meeting Link}}',  emptyFallback: DROP_LINE },
  // Scanner (primary met with)
  { object: 'Account', label: 'Scanner First Name',  variable: '{{Scanner First Name}}',   emptyFallback: DROP_LINE },
  { object: 'Account', label: 'Scanner Email',        variable: '{{Scanner Email}}',        emptyFallback: DROP_LINE },
  // Campaign
  { object: 'Campaign', label: 'Event Name',          variable: '{{Event Name}}',           emptyFallback: SUBSTITUTE('the event') },
  { object: 'Campaign', label: 'Event Date',          variable: '{{Event Date}}',           emptyFallback: DROP_LINE },
  { object: 'Campaign', label: 'Campaign Type',       variable: '{{Campaign Type}}',        emptyFallback: DROP_LINE },
  // Sender. Two of the three are Clay-owned: Clay's "Resolve sender" may pick
  // a different sender than Terra's best guess, so the NAME and the LINK are
  // deferred. `Sender Email` is not — Terra's value is the one the workflow
  // sends from, and it is a whole address rather than something a standardizer
  // rewrites.
  { object: 'Sender', label: 'Sender First Name',     variable: '{{Sender First Name}}',    emptyFallback: CLAY_OWNED },
  { object: 'Sender', label: 'Sender Email',          variable: '{{Sender Email}}',         emptyFallback: DROP_LINE },
  { object: 'Sender', label: 'Sender Meeting Link',   variable: '{{Sender Meeting Link}}',  emptyFallback: CLAY_OWNED },
  // Computed (fallback)
  { object: 'Account', label: 'Primary Seller Link',  variable: '{{Primary Seller Link}}',  emptyFallback: DROP_LINE },
];

// ── Client-side template interpolation ───────────────────────────────────────

export function resolveSenderEmail(sender: SenderValue | null, member: CampaignMember): string | null {
  if (!sender) return null;
  if (sender.type === 'token') {
    const map: Record<string, string | null> = {
      gtme_owner_email:    member.gtme_owner_email ?? null,
      gs_owner_email:      member.gs_owner_email ?? null,
      claydr_email:        member.claydr_email ?? null,
      met_with_email:      member.primary_met_with_email ?? null,
      gtme_meeting_link:   member.gtme_meeting_link ?? null,
      gs_meeting_link:     member.gs_meeting_link ?? null,
      claydr_meeting_link: member.claydr_meeting_link ?? null,
    };
    return map[sender.token ?? ''] ?? null;
  }
  return sender.email ?? null;
}

export function resolveSenderFirstName(sender: SenderValue | null, member: CampaignMember): string {
  if (!sender) return '';
  if (sender.type === 'token') {
    const map: Record<string, string> = {
      gtme_owner_email: member.gtme_owner_first_name ?? member.gtme_owner_name?.split(' ')[0] ?? '',
      gs_owner_email:   member.gs_owner_first_name   ?? member.gs_owner_name?.split(' ')[0]   ?? '',
      claydr_email:     member.claydr_first_name      ?? member.claydr_name?.split(' ')[0]     ?? '',
      met_with_email:
        member.primary_met_with_first_name ?? member.primary_met_with_name?.split(' ')[0] ?? '',
    };
    return map[sender.token ?? ''] ?? '';
  }
  const local = (sender.email ?? '').split('@')[0];
  const first = local.split('.')[0];
  return first ? first.charAt(0).toUpperCase() + first.slice(1) : '';
}

export function resolveSenderMeetingLink(sender: SenderValue | null, member: CampaignMember): string {
  if (!sender || sender.type !== 'token') return '';
  const map: Record<string, string> = {
    gtme_owner_email: member.gtme_meeting_link ?? '',
    gs_owner_email:   member.gs_meeting_link   ?? '',
    claydr_email:     member.claydr_meeting_link ?? '',
    // No Terra-side link for a scanner — Clay's `Get Sender` resolves it from
    // the SF User (verified 2026-09-22).
    met_with_email:   '',
  };
  return map[sender.token ?? ''] ?? '';
}

/**
 * The full merge-token vocabulary for one member, resolved against a
 * campaign + sender — extracted from `interpolateTemplate`'s own inline
 * `vars` map (Launcher V2 C2, plan `2026-09-01-001`) so both it and
 * `interpolateTemplateSegments` resolve against exactly one source of truth
 * instead of two copies that can drift.
 */
export function buildTemplateVars(
  member: CampaignMember,
  campaign: { name?: string; event_date?: string | null; start_date?: string | null; type?: string } | null,
  sender: SenderValue | null,
): Record<string, string> {
  const nameParts = (member.contact_name ?? '').trim().split(/\s+/);
  const firstName = nameParts[0] ?? '';
  const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : '';
  const senderEmail = resolveSenderEmail(sender, member);
  const senderFirstName = resolveSenderFirstName(sender, member);
  const senderMeetingLink = resolveSenderMeetingLink(sender, member);
  const eventDate = campaign?.event_date ?? campaign?.start_date ?? '';

  return {
    'First Name':           firstName,
    'Last Name':            lastName,
    'Full Name':            member.contact_name ?? '',
    'Email':                member.contact_email ?? '',
    'Title':                member.contact_title ?? '',
    'Campaign Status':      member.cm_status ?? '',
    'Owner Role':           member.owner_role ?? '',
    'Fit Score':            String(member.fit_score ?? ''),
    'Account Name':         member.account_name ?? '',
    'Sales Tier':           member.sales_tier ?? '',
    'GTME Owner Name':       member.gtme_owner_name ?? '',
    'GTME Owner First Name': member.gtme_owner_first_name ?? member.gtme_owner_name?.split(' ')[0] ?? '',
    'GTME Owner Email':     member.gtme_owner_email ?? '',
    'GTME Meeting Link':    member.gtme_meeting_link ?? '',
    'GS Owner Name':        member.gs_owner_name ?? '',
    'GS Owner First Name':  member.gs_owner_first_name ?? member.gs_owner_name?.split(' ')[0] ?? '',
    'GS Owner Email':       member.gs_owner_email ?? '',
    'GS Meeting Link':      member.gs_meeting_link ?? '',
    'ClayDR Name':          member.claydr_name ?? '',
    'ClayDR First Name':    member.claydr_first_name ?? member.claydr_name?.split(' ')[0] ?? '',
    'ClayDR Email':         member.claydr_email ?? '',
    'ClayDR Meeting Link':  member.claydr_meeting_link ?? '',
    'Scanner First Name':   member.primary_met_with_first_name ?? member.primary_met_with_name?.split(' ')[0] ?? '',
    'Scanner Email':        member.primary_met_with_email ?? '',
    'Primary Seller Link':  member.gtme_meeting_link || member.claydr_meeting_link || '',
    'Event Name':           campaign?.name ?? '',
    'Event Date':           eventDate,
    'Campaign Type':        campaign?.type ?? '',
    'Sender First Name':    senderFirstName,
    'Sender Email':         senderEmail ?? '',
    'Sender Meeting Link':  senderMeetingLink,
  };
}

/** Own-key check without `hasOwnProperty.call` (ES2020 lib; keeps the shared-code linter quiet). */
function hasTemplateVar(vars: Record<string, string>, key: string): boolean {
  return Object.getOwnPropertyDescriptor(vars, key) !== undefined;
}

// ── The one resolver ─────────────────────────────────────────────────────────

/**
 * The `{{Token}}` scanner. Fresh per use — a `/g` regex carries `lastIndex`.
 *
 * `[^}]*`, NOT `[^}]+` (U1, Codex A6): empty braces are a real thing reps type
 * (a half-deleted token, a paste that lost its name), and requiring at least
 * one character made `{{}}` invisible to the resolver while the authoring
 * scanner (`playbooks/sequence-token-flags.ts`, which already used `[^}]*`)
 * flagged the very same placeholder. Authoring and send now agree: `{{}}` is
 * an unknown token with the key `''`.
 */
const TOKEN_PATTERN_SOURCE = /\{\{([^}]*)\}\}/g;

/** Every token label, for the classifier and for `usedTemplateVars`. */
const KNOWN_TOKEN_LABELS: ReadonlySet<string> = new Set(COPY_VARIABLE_FIELDS.map((f) => f.label));

/**
 * The ONLY `emptyFallback` lookup in the codebase (R3). Built once from the
 * vocabulary; total because every `buildTemplateVars` key has a row
 * (`__tests__/launcher-copy-tokens-fallback.test.ts`).
 *
 * Exported so `playbooks/send.ts`'s roster-wide resolution report can read
 * this same declaration instead of keeping its own copy (Key Technical
 * Decision 2) — the report READS the vocabulary's declaration, it never
 * restates it, so a fallback changed in {@link COPY_VARIABLE_FIELDS} changes
 * what the rep is told with no edit at that call site.
 *
 * A {@link CLAY_OWNED_TOKENS} row is ABSENT from this map, not present with a
 * null value: its `emptyFallback` is `null` because the empty-value question
 * does not arise on Terra's side of the handoff, and `classifyToken` answers
 * `deferred` before it would ever look here. Absence keeps every reader
 * ("does this token have a declared policy?") answering correctly with a
 * plain `.has`/`.get` instead of a two-step null check.
 */
export const EMPTY_FALLBACK_BY_LABEL: ReadonlyMap<string, EmptyFallback> = new Map(
  COPY_VARIABLE_FIELDS.flatMap((f) =>
    f.emptyFallback === null ? [] : [[f.label, f.emptyFallback] as const],
  ),
);

/**
 * The four states a `{{Token}}` can be in, and the whole basis of the
 * three-rule outcome:
 *
 * - `deferred` — a {@link CLAY_OWNED_TOKENS} member. Checked FIRST, before
 *   `vars` and before the vocabulary, because the answer does not depend on
 *   either: whatever Terra knows (and for two of the three it knows a decent
 *   guess), the value that ships is Clay's. The literal stays in the text and
 *   the token is reported on `ResolvedCopy.deferred`.
 * - `filled` — in the vars map with a non-empty value. Substitute it.
 * - `empty` — in the vars map, value `''`. A DATA GAP: the token's declared
 *   `emptyFallback` fires and the send proceeds.
 * - `unknown` — not in the vars map, OR not in the VOCABULARY (a typo, a
 *   Gong-native spelling, `{{}}`). A COPY DEFECT: the literal stays and the
 *   caller is told via `ok: false`. `{{Company Name}}` used to land here and
 *   no longer does; a TYPO of a Clay-owned token still does, which is the
 *   property that keeps the allowlist from swallowing real defects.
 *
 * Membership is tested against `vars` AND against
 * {@link COPY_VARIABLE_FIELDS} (U1, Codex A7). `vars` alone is not enough: a
 * caller that hand-builds its map (launcher's `buildMemberPayload` overrides
 * `Sender Email`) could put a key in it that has no vocabulary row, and the
 * `empty` branch would then have no declared fallback to apply — the old code
 * silently substituted `''`, turning a typo into a hole in the sentence.
 * A key outside the vocabulary is a defect however it got into `vars`.
 *
 * The two memberships are pinned equal for the production map by
 * `__tests__/launcher-copy-tokens-fallback.test.ts`, so this AND costs
 * nothing on the real path — it only closes the exported contract.
 */
function classifyToken(
  key: string,
  vars: Record<string, string>,
  deferToClay?: readonly string[],
): 'filled' | 'empty' | 'unknown' | 'deferred' {
  if (deferredCanonicalOf(key, deferToClay) !== null) return 'deferred';
  if (!hasTemplateVar(vars, key) || !EMPTY_FALLBACK_BY_LABEL.has(key)) return 'unknown';
  return vars[key] === '' ? 'empty' : 'filled';
}

/** One `emptyFallback` that actually fired, in first-application order. */
export type AppliedFallback = { token: string; kind: EmptyFallback['kind'] };

/**
 * Every `{{…}}` token literally present in `text` — trimmed keys, first-seen
 * order, deduped, INCLUDING the Clay-owned ones. The raw scan; the two
 * exported readers below partition its output.
 *
 * Private because no caller wants "every brace" — every caller wants either
 * "every brace that is a DEFECT" ({@link residualTokens}) or "every brace that
 * is a deliberate HANDOFF" ({@link residualDeferredTokens}), and a caller that
 * took the union and then had to remember which half was which is exactly how
 * a Clay-owned literal would get reported as a copy defect at one site and not
 * another.
 */
function allResidualTokens(text: string): string[] {
  const pattern = new RegExp(TOKEN_PATTERN_SOURCE.source, 'g');
  const found: string[] = [];
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(text)) !== null) {
    const key = match[1].trim();
    if (!found.includes(key)) found.push(key);
  }
  return found;
}

/**
 * Every `{{…}}` token still literally present in `text` THAT IS A DEFECT —
 * trimmed keys, in first-seen order, deduped, with exactly
 * {@link CLAY_OWNED_TOKENS} exempted (allowlisted by trimmed key,
 * case-insensitively, so `{{ company name }}` is exempt too).
 *
 * THE EXEMPTION IS THE POINT, not a leak. A Clay-owned literal in the finished
 * text is the deliberate handoff this module exists to make, so a scan whose
 * whole job is "did a literal escape" must not report it — and the tokens it
 * exempts are named by one exported constant that the Clay `Render copy` node
 * is contractually the other half of. A residual token this function DOES
 * report is still a hard defect: nothing downstream fills it.
 *
 * WHY THIS EXISTS (U1, recommendation (c); Codex A5). Classifying the tokens
 * in the INPUT can never be sufficient, because substitution itself can
 * introduce one: a member whose `account_name` is literally
 * `'{{Company Name}}'` makes `Hi {{Account Name}}` resolve to
 * `Hi {{Company Name}}` with every input token cleanly classified. Nothing
 * upstream of the finished string can see that. So the finished string is what
 * gets checked — {@link resolveCopy} scans its own output, and the payload
 * builders scan it again independently (defense in depth).
 *
 * What it does NOT see is an UNCLOSED `{{` with no `}}` after it. That is not
 * a merge token in any dialect — Gong's truncation bug needs a complete
 * `{{name with spaces}}` to corrupt — so it stays the rep's prose, not a
 * defect that skips the member.
 *
 * Fresh `RegExp` per call: a `/g` regex carries mutable `lastIndex`.
 */
export function residualTokens(text: string, deferToClay?: readonly string[]): string[] {
  return allResidualTokens(text).filter((key) => deferredCanonicalOf(key, deferToClay) === null);
}

/**
 * {@link residualTokens}' complement — the Clay-owned tokens literally present
 * in `text`, CANONICALIZED to their declared spelling and deduped in
 * first-seen order.
 *
 * The independent second reading of a deliberate handoff, mirroring the
 * independent second reading of a defect: `resolveCopy` already reports
 * `deferred` from its own substitution pass, and the payload builder unions
 * that with this scan of the FINISHED text, so a Clay-owned token a
 * substituted member VALUE brought in (an `account_name` of literally
 * `{{Company Name}}`) still lands in the payload's `deferred_tokens` rather
 * than travelling to Clay unannounced.
 */
export function residualDeferredTokens(text: string, deferToClay?: readonly string[]): string[] {
  const found: string[] = [];
  for (const key of allResidualTokens(text)) {
    const canonical = deferredCanonicalOf(key, deferToClay);
    if (canonical !== null && !found.includes(canonical)) found.push(canonical);
  }
  return found;
}

/**
 * The resolver's outcome, a discriminated union on `ok` so the send path is
 * TYPE-FORCED to branch rather than able to post `text` blindly.
 *
 * `text` always has known tokens substituted (empties through their declared
 * fallback) and unknown tokens left literal — so a preview can show the rep
 * precisely what is broken while the send path refuses to post it.
 *
 * THE CONTRACT (U1 recommendation (c), revised 2026-09-16): `ok: true` ⇒
 * `text` contains no `{{…}}` token OTHER THAN a {@link CLAY_OWNED_TOKENS}
 * member. Not "no token the classifier recognised" — nothing else at all,
 * because the resolver rescans its own finished output with
 * {@link residualTokens} (which exempts exactly that set and nothing else).
 * That is what makes `ok` safe to branch a SEND on: Gong truncates a literal
 * `{{First Name}}` at the first space and renders `{{First}}` in red (verified
 * live 2026-09-15), so "no UNFILLED literal leaves Terra" has to be a property
 * of the string, not of the bookkeeping. The Clay-owned exemption is safe
 * precisely because it is not a gap in that property: Clay's `Render copy`
 * node fills those and hard-stops if it cannot, so they are never what reaches
 * Gong.
 *
 * `deferred` is every Clay-owned token left literally in the FINISHED `text`,
 * canonicalized to its declared spelling, first-seen order, deduped — read off
 * that string rather than accumulated while substituting, because it is a
 * claim ABOUT that string (see the scan's own comment for the two ways the two
 * readings differ). It is present on BOTH branches of the union: a member can
 * have a real copy defect AND a deliberate handoff in the same step, and
 * folding the second into the first is exactly the confusion this state exists
 * to end.
 *
 * `filled` (Playbooks send fix plan U10, Codex B4) is every KNOWN token that
 * was actually substituted with a non-empty value — first-seen order, deduped.
 * It exists so a caller can compute "members whose copy USED this token" as
 * `filled ∪ empty`, instead of the whole routed roster: a token this copy
 * never references should not count every member who lacks it as "resolved"
 * for it.
 */
export type ResolvedCopy =
  | {
      ok: true;
      text: string;
      empty: string[];
      filled: string[];
      deferred: string[];
      fallbacks: AppliedFallback[];
      valueDefects?: ValueDefect[];
    }
  | {
      ok: false;
      text: string;
      empty: string[];
      filled: string[];
      deferred: string[];
      fallbacks: AppliedFallback[];
      unknown: string[];
      valueDefects?: ValueDefect[];
    };

/**
 * A member VALUE that is itself merge-token shaped (a contact whose first name
 * in Salesforce is literally `{{unknown}}`) — plan 2026-10-14-002 U0f. A data
 * gap in the record, not a typo in the copy.
 *
 * Present on {@link ResolvedCopy} ONLY when the caller opted in with
 * {@link ResolveCopyOptions.treatTokenShapedValuesAsEmpty}; a bare call never
 * carries the key, so Launcher's and Flow's results are unchanged.
 */
export type ValueDefect = { token: string; value: string };

/**
 * Opt-in behaviours of {@link resolveCopy}. Every flag defaults to off, and a
 * caller that passes nothing gets the resolver exactly as it was before the
 * flag existed. (`field` is still a required positional argument, never an
 * option — see the note on `resolveCopy`.)
 */
export type ResolveCopyOptions = {
  /**
   * A `filled` value that itself contains a `{{…}}` DEFECT token
   * ({@link residualTokens} — so a Clay-owned handoff value such as
   * `{{Company Name}}` is not one) is treated as `empty`: the token's declared
   * fallback fires (`there` for First Name), the member still sends, and the
   * token and value are recorded on `valueDefects`. Playbooks passes this;
   * Launcher and Flow never do, so for them such a value still resolves
   * verbatim and the finished-text scan holds the member back as before.
   */
  treatTokenShapedValuesAsEmpty?: boolean;
  /**
   * Extra tokens (by declared label) to leave LITERAL for Clay, on top of
   * {@link CLAY_OWNED_TOKENS} — the send path passes
   * {@link CLAY_RENDERED_AT_SEND_TOKENS}. A listed token is `deferred`: the
   * literal stays (re-braced in its declared spelling), nothing is filled and
   * no empty fallback fires.
   *
   * Decided BEFORE `treatTokenShapedValuesAsEmpty`: on a send, a first name of
   * literally `{{unknown}}` is not recorded as a `valueDefect`, because Terra
   * is not substituting it — Clay's standardizer decides. Previews (which do
   * not pass this) still record it.
   *
   * Default off.
   */
  deferToClay?: readonly string[];
};

/**
 * `ResolvedCopy`'s `unknown` list, or `[]` when the resolution was clean.
 *
 * Promoted here from two identical private copies (`launcher/api.ts` and
 * `playbooks/send-payload.ts`) — both did `resolved.ok ? [] : resolved.unknown`
 * next to a call site that folds it with a fresh `residualTokens` scan. One
 * shared reader of `ResolvedCopy.unknown` next to the type it operates on.
 */
export function unknownOf(resolved: ResolvedCopy): string[] {
  return resolved.ok ? [] : resolved.unknown;
}

/**
 * First-seen-order string dedupe — the order every token list on this module's
 * results is in (`ResolvedCopy.empty/filled/unknown`, `StepResolution`,
 * `MemberPayloadResolution`).
 *
 * Promoted here from two identical private copies (`playbooks/send-payload.ts`'s
 * `dedupe` and `launcher/api.ts`'s `dedupeTokens`) — same body, same `O(n²)`
 * `includes` scan (token lists here are small enough per member that this
 * never shows up), two names for the same function.
 */
export function dedupeTokens(values: readonly string[]): string[] {
  const out: string[] = [];
  for (const value of values) if (!out.includes(value)) out.push(value);
  return out;
}

/**
 * Is this string HTML? ANY tag-like token answers yes (U3, Codex A1/A2).
 *
 * WAS a whitelist of tag NAMES copied from `playbooks/html-format.ts`, and it
 * was the wrong question in this one place. The whitelist has no
 * `table/tr/td`, so a table-only body answered "no" and took the PLAIN-TEXT
 * newline rule — and a serialized HTML document is frequently one physical
 * line, so "remove the enclosing line" removed the entire body. Both of the
 * catastrophic deletions Codex found (A1: a table-only body → `''`; A2: an
 * `<html><body>…` envelope → `''`) are that one misclassification.
 *
 * So this is deliberately over-inclusive, because the two errors are not
 * symmetric. A false NEGATIVE runs the newline rule inside markup and can
 * delete a whole document. A false POSITIVE — `<Acme>` or `<pizza>` written as
 * a bracket placeholder in plain prose — means the token is removed and no
 * line is, which is the safe degrade this unit exists to guarantee. The rule
 * is: inside anything that might be HTML, only a VERIFIED block may be
 * deleted; nothing else, ever.
 *
 * Non-global on purpose — a `/g` regex carries `lastIndex` across `.test`.
 */
const HTML_TAG_RE = /<\/?[a-zA-Z][^<>]*>/;

/**
 * The block elements a `drop-line` removal may delete whole: the tags that
 * wrap ONE visual line of copy. `ul`/`ol` are absent (deleting a list because
 * one item lost its link is too much); `li` is present (that one item is the
 * line).
 *
 * QUOTE-AWARE (U3, Codex A3). The attribute run is
 * `(?:"[^"]*"|'[^']*'|[^"'>])*`, not `[^>]*`, so a `>` inside an attribute
 * VALUE does not end the tag. With `[^>]*`, the open tag of
 * `<p title="x > y">` ended at the `>` in the attribute and the scanner then
 * treated the rest of the value as markup — including a quoted `</p>`, which
 * made a removal cut through the middle of an attribute and leave `">` as
 * visible text in the email.
 *
 * The three alternatives are mutually exclusive on their first character, so
 * the star cannot backtrack ambiguously: this stays linear. A tag with an
 * UNCLOSED quote matches nothing at all, which degrades to token-only removal
 * rather than to a guess.
 *
 * Group 1 = the `/` of a close tag, group 2 = the tag name, group 3 = the
 * attribute run (read to detect `<div/>`-style self-closing tags).
 */
const DROP_BLOCK_TAG_RE =
  /<(\/?)(p|div|li|h[1-6]|blockquote|td|th)(?=[\s/>])((?:"[^"]*"|'[^']*'|[^"'>])*)>/gi;

/**
 * A `<br>` — the line break INSIDE a block. Quote-aware for the same reason
 * the block scanner is; `(?=[\s/>])` rejects `<br0>`/`<break>`.
 */
const BR_TAG_RE = /<br(?=[\s/>])((?:"[^"]*"|'[^']*'|[^"'>])*)>/gi;

/**
 * Where a `drop-line` token stood, held in the resolved text until the
 * line-removal pass. NUL-delimited so it cannot collide with real copy, real
 * markup, or the tag scanners above.
 *
 * BUILT AT RUNTIME VIA `String.fromCharCode`, NEVER AS A UNICODE-ESCAPE
 * STRING LITERAL, and please keep it that way (in this comment too):
 * spelling that escape out in source risks it being SAVED as a real NUL byte,
 * which makes this file binary as far as `grep`, `git diff` and the linters
 * are concerned — `grep` then silently reports no matches in it instead of
 * erroring, so a "prints nothing" verification gate passes while actually
 * having skipped the file. `String.fromCharCode(0)` keeps the source plain
 * ASCII and the runtime value identical.
 */
const SENTINEL_CHAR = String.fromCharCode(0);
const DROP_MARKER = `${SENTINEL_CHAR}drop-line${SENTINEL_CHAR}`;

/**
 * A block whose FULL EXTENT the scanner established — the open tag, the close
 * (real or implied), and the inner text between them.
 *
 * "Verified" is the load-bearing word (U3). Only a block that actually ended
 * somewhere may be deleted; an open tag whose end was never established is
 * markup the scanner does not understand, and the answer for that is to remove
 * the token, not to guess at an extent and delete content.
 */
interface VerifiedBlock {
  start: number;
  end: number;
  innerStart: number;
  innerEnd: number;
}

/** One open block on the scanner's stack. */
interface OpenBlock {
  name: string;
  start: number;
  /** Just past this tag's `>` — where its inner text begins. */
  innerStart: number;
}

/**
 * An open `<p>` that a later tag implicitly closed: HTML permits an omitted
 * `</p>`, and a paragraph ends when the next block opens or its parent closes.
 * Pops every `p` off the top of `open` and, if one of them contained `at`,
 * returns it as a verified block ending at `endsAt`.
 *
 * WHY (U3, Codex A3). The old stack never closed those paragraphs, so in
 * `<div><p>Hi<p>{{Token}}<p>Bye</div>` the first close tag that matched
 * anything on the stack was the `</div>` — and one empty title therefore
 * deleted the entire container. Only `p` gets this treatment: it is the tag
 * whose end tag HTML most commonly omits, and every other omission degrades
 * to token-only removal, which is safe.
 */
function closeImplicitParagraphs(
  open: OpenBlock[],
  at: number,
  endsAt: number,
  floor: number,
): VerifiedBlock | null {
  let found: VerifiedBlock | null = null;
  while (open.length > floor && open[open.length - 1].name === 'p') {
    const entry = open[open.length - 1];
    open.length -= 1;
    if (found === null && entry.start <= at && at < endsAt) {
      found = { start: entry.start, end: endsAt, innerStart: entry.innerStart, innerEnd: endsAt };
    }
  }
  return found;
}

/**
 * The innermost VERIFIED block enclosing `at`, or `null` when there is none.
 *
 * Tokenizes the block tags in one forward pass and keeps a stack, so the
 * FIRST element that closes while still containing `at` is by construction the
 * innermost one: `<div><p>{{Token}}</p></div>` yields the `<p>`, never the
 * `<div>`. A close tag that does not match the open stack's top is tolerated
 * by unwinding to the nearest matching name (Gong-authored HTML is not
 * guaranteed well-formed, and a resolver must not throw over markup).
 *
 * Returning `null` is a real and frequent answer, not a failure: an `<html>`
 * envelope, a `<span>`, a `<strong>`, or an unclosed `<p>` at the end of the
 * string all produce it, and the caller's response is to remove the token
 * alone. That is the whole safety property of this pass.
 */
function enclosingBlockRange(s: string, at: number): VerifiedBlock | null {
  const pattern = new RegExp(DROP_BLOCK_TAG_RE.source, 'gi');
  const open: OpenBlock[] = [];
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(s)) !== null) {
    const isClose = match[1] === '/';
    const name = match[2].toLowerCase();
    const tagEnd = match.index + match[0].length;

    if (!isClose) {
      // `<div/>` — an XHTML-style self-closing tag opens nothing. Pushing it
      // would leave a phantom block open for the rest of the document.
      if (match[3].trimEnd().endsWith('/')) continue;
      const implied = closeImplicitParagraphs(open, at, match.index, 0);
      if (implied !== null) return implied;
      open.push({ name, start: match.index, innerStart: tagEnd });
      continue;
    }

    // Nearest matching open tag, searched from the top of the stack down. A
    // manual loop rather than `findLastIndex` because the root tsconfig's lib
    // is ES2022 and that method is ES2023.
    let depth = -1;
    for (let i = open.length - 1; i >= 0; i--) {
      if (open[i].name === name) {
        depth = i;
        break;
      }
    }
    if (depth === -1) continue;
    // Anything still open ABOVE the match closes here. A `<p>` closes
    // implicitly and legitimately; anything else is unbalanced markup whose
    // extent was never established, so it is discarded rather than deleted.
    const implied = closeImplicitParagraphs(open, at, match.index, depth + 1);
    if (implied !== null) return implied;
    const entry = open[depth];
    open.length = depth;
    if (entry.start <= at && at < tagEnd) {
      return { start: entry.start, end: tagEnd, innerStart: entry.innerStart, innerEnd: match.index };
    }
  }
  return null;
}

/**
 * The plain-text line holding `at`, as a `[start, end)` range: from just after
 * the previous `\n` through the next `\n` inclusive.
 *
 * When there is no next `\n` (the token sits on the LAST line) the removal
 * takes the PRECEDING newline instead, so dropping the final line does not
 * leave a dangling blank one behind.
 */
function enclosingLineRange(s: string, at: number): { start: number; end: number } {
  const prev = s.lastIndexOf('\n', at);
  const next = s.indexOf('\n', at);
  if (next === -1) return { start: prev === -1 ? 0 : prev, end: s.length };
  return { start: prev === -1 ? 0 : prev + 1, end: next + 1 };
}

/**
 * What a `drop-line` removal takes, inside HTML: the innermost VERIFIED block
 * holding `at`, narrowed to that block's `<br>`-delimited segment when it has
 * any. `null` when no block could be verified.
 *
 * ── WHY THE `<br>` NARROWING ────────────────────────────────────────────────
 * "One visual line" has two spellings in the HTML Gong and Terra produce: one
 * block per line (`<div data-default-styles>` per line, `<li>` per item), and
 * one block with `<br>`-separated lines inside it. The block rule alone is
 * right for the first and much too coarse for the second — a paragraph holding
 * a greeting, a CTA and a sign-off would lose all three because the CTA's link
 * was empty. Inside a block whose extent is known, a `<br>` segment IS the
 * line, and the last-segment rule mirrors {@link enclosingLineRange}'s
 * (take the PRECEDING break, so removing the final line leaves no trailing
 * blank one).
 *
 * Deliberately scoped to the inside of a verified block. Applying it to a
 * `<br>` in unverified markup would be the newline rule all over again —
 * guessing at a boundary from a delimiter whose enclosing extent is unknown —
 * which is exactly what U3 exists to stop (`<html><body>Hi<br>{{Token}}<br>Bye`
 * keeps both breaks and loses only the token).
 */
function enclosingDropRange(s: string, at: number): { start: number; end: number } | null {
  const block = enclosingBlockRange(s, at);
  if (block === null) return null;
  const whole = { start: block.start, end: block.end };
  // The marker can sit inside the OPEN TAG itself (a token inside an attribute
  // value). There is no inner-text segment to narrow to then — take the block.
  if (at < block.innerStart || at >= block.innerEnd) return whole;

  const pattern = new RegExp(BR_TAG_RE.source, 'gi');
  pattern.lastIndex = block.innerStart;
  let previous: { start: number; end: number } | null = null;
  let next: { start: number; end: number } | null = null;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(s)) !== null) {
    if (match.index >= block.innerEnd) break;
    const br = { start: match.index, end: match.index + match[0].length };
    if (br.end <= at) previous = br;
    else if (next === null && br.start > at) next = br;
  }
  if (previous === null && next === null) return whole;

  const start = next !== null
    ? (previous?.end ?? block.innerStart)
    : (previous?.start ?? block.innerStart);
  const end = next !== null ? next.end : block.innerEnd;
  // A segment spanning the entire inner text is the block — take the tags too.
  return start <= block.innerStart && end >= block.innerEnd ? whole : { start, end };
}

/**
 * Remove each `DROP_MARKER`'s enclosing line/block.
 *
 * Always works on the LAST marker in the current string and re-scans after
 * every removal, which is what makes two drop-line tokens on one line remove
 * that line exactly ONCE (the rightmost removal already swallowed the
 * leftmost marker) without any offset bookkeeping.
 *
 * ── THE TWO BRANCHES, AND WHY THEY NO LONGER MEET (U3) ──────────────────────
 * In PLAIN TEXT a newline is a real, unambiguous boundary, so the line rule
 * stands. In HTML it is not a boundary at all — a serialized document is
 * routinely one physical line, and the tag that closes an element sits
 * wherever the serializer put it. So in HTML the newline rule is not a
 * fallback, it is simply unavailable: a `drop-line` removes the innermost
 * VERIFIED block (or that block's `<br>` segment), and when no block can be
 * verified it removes ONLY the token.
 *
 * The old code had `(isHtml ? enclosingBlockRange(…) : null) ?? enclosingLineRange(…)`
 * — the `??` ran the newline rule INSIDE HTML whenever the block scanner came
 * back empty, which is how an `<html><body>…` envelope and a `<strong>` both
 * lost their entire contents (Codex A2). Degrading to a smaller removal is
 * always safe; degrading to a differently-shaped larger one never is.
 *
 * `isHtml` is computed once, on the string BEFORE any removal: the classifier
 * must not flip to plain text mid-loop because the last removal took the only
 * tag with it.
 */
function applyDropLines(resolved: string): string {
  const isHtml = HTML_TAG_RE.test(resolved);
  let out = resolved;

  for (;;) {
    const at = out.lastIndexOf(DROP_MARKER);
    if (at === -1) return out;
    const range = isHtml
      ? (enclosingDropRange(out, at) ?? { start: at, end: at + DROP_MARKER.length })
      : enclosingLineRange(out, at);
    out = out.slice(0, range.start) + out.slice(range.end);
  }
}

/**
 * Which copy field is being resolved. A SUBJECT has no lines to drop; a BODY
 * does. Nothing else about the two differs.
 */
export type CopyField = 'subject' | 'body';

/**
 * Resolve every `{{Token}}` in `text` against `vars` — the one resolver
 * (Playbooks send PRD U1, R1/R2/R3). Never throws for a copy state.
 *
 * ── THE MODE IS A PROPERTY OF THE FIELD, NEVER OF THE CALLER ────────────────
 * `field` is REQUIRED and there is no options object and no default (U2,
 * Codex A4). It used to be `opts?: { singleLine?: boolean }`, and the send
 * path passed `{ singleLine: true }` for subjects while the preview path
 * passed nothing — so `Meet {{Title}} tomorrow` with an empty title PREVIEWED
 * as `""` (the `drop-line` fallback took the only "line" there was) and SENT
 * as `Meet tomorrow`. Sharing a function does not give you parity if its mode
 * is a per-call-site decision; making the mode a required argument derived
 * from the field does, because omitting it is now a type error and mislabeling
 * it needs a literal `'body'` typed onto a subject, which greps and reviews.
 * Step-shaped callers should use {@link resolveStepCopy} and not pick at all.
 *
 * `'subject'` ⇒ a `drop-line` token degrades to removing the token and
 * collapsing the doubled whitespace it leaves behind.
 */
export function resolveCopy(
  text: string,
  vars: Record<string, string>,
  field: CopyField,
  options?: ResolveCopyOptions,
): ResolvedCopy {
  const singleLine = field === 'subject';
  const valueDefectsOptIn = options?.treatTokenShapedValuesAsEmpty === true;
  const deferToClay = options?.deferToClay;
  const empty: string[] = [];
  const filled: string[] = [];
  const fallbacks: AppliedFallback[] = [];
  const unknown: string[] = [];
  const valueDefects: ValueDefect[] = [];

  const pattern = new RegExp(TOKEN_PATTERN_SOURCE.source, 'g');
  const resolved = text.replace(pattern, (_match, rawKey: string) => {
    const key = rawKey.trim();
    let state = classifyToken(key, vars, deferToClay);
    if (valueDefectsOptIn && state === 'filled' && residualTokens(vars[key], deferToClay).length > 0) {
      // U0f: the VALUE is a placeholder — a record gap, so the declared
      // fallback applies and the defect is recorded for the report.
      if (!valueDefects.some((d) => d.token === key)) valueDefects.push({ token: key, value: vars[key] });
      state = 'empty';
    }
    if (state === 'deferred') {
      // Re-braced with the DECLARED spelling, not the rep's: Clay's
      // `Render copy` node matches literally, so `{{ company name }}` has to
      // leave as `{{Company Name}}` or the handoff silently fails its own
      // hard-stop check on the far side.
      //
      // Nothing is recorded here — `deferred` is read off the FINISHED text
      // below, not accumulated during this pass. See the comment at that
      // scan for why.
      return `{{${deferredCanonicalOf(key, deferToClay) ?? key}}}`;
    }
    if (state === 'filled') {
      if (!filled.includes(key)) filled.push(key);
      return vars[key];
    }
    if (state === 'unknown') {
      if (!unknown.includes(key)) unknown.push(key);
      return `{{${key}}}`;
    }
    // `empty` — a data gap. The declared fallback decides, nothing else.
    if (!empty.includes(key)) empty.push(key);
    const fallback = EMPTY_FALLBACK_BY_LABEL.get(key);
    if (fallback === undefined) {
      // Genuinely unreachable since U1: `classifyToken` only answers `empty`
      // for a key that HAS a vocabulary row, so this lookup cannot miss. Kept
      // so the function stays total for the type-checker rather than asserting
      // — but it is no longer the silent `''` substitution that made an
      // out-of-vocabulary key disappear (Codex A7); that path now classifies
      // as `unknown` above and never reaches here.
      return '';
    }
    if (!fallbacks.some((f) => f.token === key)) {
      fallbacks.push({ token: key, kind: fallback.kind });
    }
    return fallback.kind === 'substitute' ? fallback.text : DROP_MARKER;
  });

  const dropped = resolved.includes(DROP_MARKER)
    ? singleLine
      ? resolved.split(DROP_MARKER).join('').replace(/\s{2,}/g, ' ').trim()
      : applyDropLines(resolved)
    : resolved;
  // A deferred token a member VALUE brought in (`account_name` of literally
  // `{{ first name }}`) is re-braced in its declared spelling too: Clay's
  // `Render copy` matches literally, and `deferred` below is announced in that
  // spelling, so the text and the announcement must agree.
  const finalText = dropped.replace(new RegExp(TOKEN_PATTERN_SOURCE.source, 'g'), (match, rawKey: string) => {
    const canonical = deferredCanonicalOf(rawKey.trim(), deferToClay);
    return canonical === null ? match : `{{${canonical}}}`;
  });

  // The contract check, on the FINISHED string (U1). Everything above reasons
  // about the tokens the input had; this is the only thing that can see a
  // token a substituted VALUE brought in with it (Codex A5), and it runs after
  // the drop-line pass so it also sees whatever that pass left behind.
  //
  // Both halves of the partition get the same treatment, deliberately: a
  // substituted value can introduce a Clay-owned token just as easily as a
  // typo (a member whose `account_name` is literally `{{Company Name}}` is the
  // real case), and that is a handoff to announce, not a defect to skip the
  // member for and not something to let travel unannounced.
  for (const key of residualTokens(finalText, deferToClay)) {
    if (!unknown.includes(key)) unknown.push(key);
  }

  // `deferred` is DERIVED ENTIRELY from the finished string — unlike `empty` /
  // `filled` / `unknown`, which the substitution pass accumulates and this
  // scan only adds to.
  //
  // Because `deferred` answers a question only the finished string can answer:
  // "which Clay-owned literals are in the text Clay is about to render". Those
  // are two different sets from "which ones the INPUT mentioned", in both
  // directions. A substituted member value can INTRODUCE one (an
  // `account_name` of literally `{{Company Name}}`). And the `drop-line` pass
  // can REMOVE one as collateral: in
  // `<p>From {{Sender First Name}}, book <a href="{{GS Meeting Link}}">here</a></p>`
  // an empty meeting link takes the whole block, sender token included — so
  // the copy no longer defers it, and announcing it on the payload's
  // `deferred_tokens` would hand Clay a token that is not in the text to find.
  const deferred = residualDeferredTokens(finalText, deferToClay);

  // `valueDefects` is spread in only for an opted-in caller, so a bare call's
  // result has exactly the keys it always had.
  const extra = valueDefectsOptIn ? { valueDefects } : {};
  return unknown.length > 0
    ? { ok: false, text: finalText, empty, filled, deferred, fallbacks, unknown, ...extra }
    : { ok: true, text: finalText, empty, filled, deferred, fallbacks, ...extra };
}

/**
 * Resolve BOTH copy fields of one step, each in its own mode — the single
 * entry point for every step-shaped caller (U2, recommendation (b)):
 * `toOverrideSteps` (the send), `previewStepCopy` (the Playbooks preview) and
 * launcher's `buildMemberPayload`.
 *
 * Why this exists on top of a required `field` argument: those three callers
 * all hold a `{ subject, body }` pair, and each one picking its own two field
 * literals is three chances to pick wrong. Here the pairing is written once.
 * A caller that genuinely holds ONE field (a subject-only chip) still calls
 * {@link resolveCopy} directly rather than fabricating a step object.
 */
export function resolveStepCopy(
  step: { subject?: string | null; body?: string | null },
  vars: Record<string, string>,
  options?: ResolveCopyOptions,
): { subject: ResolvedCopy; body: ResolvedCopy } {
  return {
    subject: resolveCopy(step.subject ?? '', vars, 'subject', options),
    body: resolveCopy(step.body ?? '', vars, 'body', options),
  };
}

/**
 * The preview face of {@link resolveCopy} — same resolver, same declared
 * fallbacks, so the copy a rep reads in the preview is byte-identical to the
 * copy that leaves for Gong (R1; the parity assertion lives in
 * `__tests__/launcher-copy-tokens-segments.test.ts`).
 *
 * `field` is REQUIRED for the same reason it is on `resolveCopy`, and this
 * wrapper is exactly where the old bug lived: it forwarded no mode, so every
 * caller of it previewed subjects in BODY mode while the send path used
 * subject mode (U2, Codex A4). Launcher's `CopyEditor`/`WaterfallConfig`
 * subject previews were the rep-visible face of that divergence.
 *
 * UNKNOWN TOKENS STAY LITERAL, BY DESIGN. This returns a bare string and so
 * has nowhere to report a copy defect; leaving `{{Frist Name}}` visible is the
 * honest rendering, and the authoring FLAG (U2/U8) — not the copy — is what
 * tells the rep to fix it. A caller that must not SHIP a defect calls
 * `resolveCopy` and branches on `ok`.
 *
 * CLAY-OWNED TOKENS ALSO STAY LITERAL HERE, and for a stronger reason than
 * "nowhere to report it": this is the FINAL-COPY face of the resolver, so its
 * output is byte-identical to what the payload carries — and the payload
 * carries the literal `{{Company Name}}`. Substituting Terra's best guess
 * would make this string a preview of something Terra does not send.
 * {@link interpolateTemplateSegments} is where the guess belongs: a segment
 * can carry BOTH the guess (as its text) and the caveat (as a distinctly
 * styled `deferred` chip captioned {@link CLAY_OWNED_TOKEN_CAPTION}), which is
 * exactly what a bare string cannot do.
 */
export function interpolateTemplate(
  template: string,
  member: CampaignMember,
  campaign: { name?: string; event_date?: string | null; start_date?: string | null; type?: string } | null,
  sender: SenderValue | null,
  field: CopyField,
): string {
  return resolveCopy(template, buildTemplateVars(member, campaign, sender), field).text;
}

/**
 * One rendered piece of a template: literal text, a resolved value, a
 * known-but-empty token, a Clay-owned token awaiting the handoff, or a token
 * outside the vocabulary entirely.
 *
 * `empty` and `unknown` both carry the raw `{{Token}}` text (never a blank
 * string) so a caller can render it as a visible, styled chip instead of
 * silently dropping it — matching the prototype's `interpolate()`
 * (`playbooks-prototype-v6.html:3571-3577`).
 *
 * `deferred` is the only variant that carries a second field, and it needs to:
 * its `text` is what to SHOW (Terra's best guess when it has one, so the rep
 * reads a plausible sentence rather than a placeholder), while `token` is
 * WHICH Clay-owned token it stands for, so the chip's caption can name it
 * without the caller re-parsing the text it was just handed. Terra's guess is
 * a preview aid only — the payload carries the literal token, which is exactly
 * why this chip needs a distinct style and the
 * {@link CLAY_OWNED_TOKEN_CAPTION} tooltip: it is the one segment whose
 * displayed text is knowingly not what the recipient will read.
 *
 * `text` falls back to the braced token (`{{Company Name}}`) when Terra has no
 * guess — no `buildTemplateVars` key at all (`Company Name`) or an empty value
 * (no meeting link on the guessed sender). A blank chip would read as a hole
 * in the sentence, which is the bug the whole fallback system exists to kill.
 */
export type TemplateSegment =
  | { kind: 'text'; text: string }
  | { kind: 'filled'; text: string }
  | { kind: 'empty'; text: string }
  | { kind: 'deferred'; text: string; token: string }
  | { kind: 'unknown'; text: string };

/**
 * The DIAGNOSTIC view of a template: which parts are plain text, a resolved
 * token, a known-but-empty token, or a token outside the vocabulary — so the
 * copy preview can render the last two as visible chips instead of silently
 * rendering blank.
 *
 * NOT the final copy, and deliberately not byte-comparable to it. Where
 * {@link resolveCopy} APPLIES an empty token's declared fallback (substituting
 * `there`, or removing the line), this reports the gap as an `empty` chip
 * carrying the raw `{{Token}}`. Two different jobs: this one answers "which
 * tokens did not resolve for this member", `resolveCopy` answers "what does
 * the recipient actually read". Showing the applied fallback inline in the
 * chip is deferred follow-up work, not an oversight.
 *
 * Classification goes through the same private `classifyToken` the resolver
 * uses (U1), so the two can never disagree about what counts as empty vs.
 * unknown.
 */
export function interpolateTemplateSegments(
  template: string,
  member: CampaignMember,
  campaign: { name?: string; event_date?: string | null; start_date?: string | null; type?: string } | null,
  sender: SenderValue | null,
): TemplateSegment[] {
  const vars = buildTemplateVars(member, campaign, sender);
  const segments: TemplateSegment[] = [];
  // `[^}]*` for the same reason `TOKEN_PATTERN_SOURCE` uses it (U1): `{{}}`
  // has to segment as an `unknown` chip here, or the diagnostic view and the
  // resolver disagree about the same placeholder.
  const pattern = new RegExp(TOKEN_PATTERN_SOURCE.source, 'g');
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(template)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ kind: 'text', text: template.slice(lastIndex, match.index) });
    }
    const trimmed = match[1].trim();
    const state = classifyToken(trimmed, vars);
    if (state === 'filled') {
      segments.push({ kind: 'filled', text: vars[trimmed] });
    } else if (state === 'deferred') {
      // Terra's BEST GUESS when it has a non-empty one (the sender tokens do),
      // the canonical braced token otherwise (`Company Name` never does).
      const token = clayOwnedTokenOf(trimmed) ?? trimmed;
      const guess = vars[token] ?? '';
      segments.push({ kind: 'deferred', text: guess === '' ? `{{${token}}}` : guess, token });
    } else {
      segments.push({ kind: state, text: `{{${trimmed}}}` });
    }
    lastIndex = pattern.lastIndex;
  }
  if (lastIndex < template.length) {
    segments.push({ kind: 'text', text: template.slice(lastIndex) });
  }

  return segments;
}

// ── The copy-defect contract ─────────────────────────────────────────────────

/**
 * The stable prefix on a `launcher_dispatch_log.error` written because a
 * member's copy carried an unknown token.
 *
 * It lives in `_shared` because it is a genuine CROSS-APP contract
 * (`mini-apps/CLAUDE.md` promotion rules): launcher and Playbooks both WRITE
 * it into the same `launcher_dispatch_log.error` column, and the dispatch
 * inspector READS it back by prefix to tell a copy defect apart from a
 * transport failure. Three copies of the string would be three chances to
 * drift. The string itself is canonical in
 * `src/shared/launcher-dispatch-error-prefixes.ts` (pure constants), because
 * core's Clay dispatch-result callback also needs it — to never repair a
 * skipped row to `sent` (plan 2026-09-26-003, U6).
 */
export { COPY_DEFECT_ERROR_PREFIX };

/**
 * {@link COPY_DEFECT_ERROR_PREFIX}'s sibling: the stable prefix on a
 * `launcher_dispatch_log.error` written because a member was skipped over an
 * UNRESOLVABLE SENDER rather than broken copy (QA round 8, U6 — today only
 * the met-with (scanner) sender on a member with no "met with" user).
 *
 * It lives here for exactly the reason the copy-defect prefix does: the
 * writer (`playbooks/send-payload.ts`, which composes the sentence) and the
 * reader (`playbooks/dispatch-inspector-logic.ts`, which classifies a row by
 * prefix) are two different modules, and the reader is frontend-bundled while
 * the writer sits behind the send. Pointing the reader at the writer creates
 * a real import cycle (`dispatch-inspector-logic` → `send-payload` →
 * `gong-flow-picker-api` → `api-client`), which surfaced as
 * `SyntaxError: Export named 'archiveIdentityRequest' not found` at module
 * init. One string, in the one module both sides already import.
 */
export { SENDER_MISSING_ERROR_PREFIX };

/**
 * Render a member's unknown-token occurrences as the one-line
 * `launcher_dispatch_log.error` message, e.g.
 *
 *     copy_defect: unknown token(s) {{Frist Name}} (step 1 body), {{Company Name}} (step 3 subject)
 *
 * `where` is free text the caller supplies (Playbooks passes
 * `step 3 subject`, 1-based, because that is what the rep sees in the step
 * header). Tokens are re-braced so the message reads as the thing the rep has
 * to go and find in the editor. Deduped by token AND `where`: the same typo
 * repeated inside one field is one occurrence, the same typo in a different
 * field is a second one.
 */
export function formatCopyDefectError(
  occurrences: readonly { token: string; where: string }[],
): string {
  const seen = new Set<string>();
  const parts: string[] = [];
  for (const { token, where } of occurrences) {
    const dedupeKey = `${token}${SENTINEL_CHAR}${where}`;
    if (seen.has(dedupeKey)) continue;
    seen.add(dedupeKey);
    parts.push(`{{${token}}} (${where})`);
  }
  const listed = parts.length === 0 ? '' : ` ${parts.join(', ')}`;
  return `${COPY_DEFECT_ERROR_PREFIX} unknown token(s)${listed}`;
}

// ── Template variable gap detection ──────────────────────────────────────────

/**
 * The KNOWN `{{Token}}` names this copy actually uses. Extracted ONCE per copy
 * — the whole point of {@link checkCopyVarGapsByMemberId}'s existence (see its
 * doc).
 *
 * Filtered to {@link COPY_VARIABLE_FIELDS} labels (U1) MINUS
 * {@link CLAY_OWNED_TOKENS}. Both exclusions exist for the same reason — no
 * member can ever "have" the token, so letting it through here would report
 * every member as missing data and bury the real gaps — but they are different
 * failures. A token outside the vocabulary (`{{Frist Name}}`, a Gong-native
 * spelling) is a COPY DEFECT, surfaced through `resolveCopy`'s `ok: false` and
 * the authoring flags. A Clay-owned token is not a failure at all: Terra is
 * not supposed to have it. Counting `{{Company Name}}` as a gap for all 400
 * members of a roster would be a report about a value Clay is about to fill
 * correctly.
 *
 * `{{Sender First Name}}` / `{{Sender Meeting Link}}` are excluded even though
 * `buildTemplateVars` DOES emit a guess for them: the gap report answers "will
 * the recipient read a hole here", and the answer for those is no regardless
 * of Terra's guess.
 *
 * A fresh `RegExp` per call rather than a module-scope one: a `/g` regex
 * carries mutable `lastIndex`, so sharing one instance across calls (or
 * across the two `exec` loops below) would make the scan order-dependent.
 */
function usedTemplateVars(subject: string, body: string): Set<string> {
  const usedVars = new Set<string>();
  const varPattern = new RegExp(TOKEN_PATTERN_SOURCE.source, 'g');
  for (const text of [subject, body]) {
    varPattern.lastIndex = 0;
    let m;
    while ((m = varPattern.exec(text)) !== null) {
      const trimmed = m[1].trim();
      if (KNOWN_TOKEN_LABELS.has(trimmed) && !isClayOwnedToken(trimmed)) usedVars.add(trimmed);
    }
  }
  return usedVars;
}

/**
 * Playbooks QA round 4, U11 (E1) / Decision 14 — the gap check keyed by
 * `member.id`, and the primitive {@link checkCopyVarGaps} is now built on.
 *
 * ── WHY (the E1 typing lag) ───────────────────────────────────────────────
 * `mini-apps/playbooks/copy-token-flags.ts#rosterTokenGaps` needs
 * collision-free `memberIds` (two members can share a display name), which
 * `checkCopyVarGaps`' `memberNames` aggregation cannot give it. So it used to
 * call `checkCopyVarGaps` ONCE PER MEMBER with a one-element array — and
 * every one of those calls re-ran the `{{...}}` regex over the whole joined
 * subject+body. On a 400-member roster that is 400 full scans of the copy per
 * keystroke: O(M·len) work in the keystroke's own commit path.
 *
 * This extracts `usedVars` once and then loops members: O(len + M·vars).
 * Returns `variable -> memberIds[]`, in roster order within each variable, so
 * the caller's shape transform is a plain `map`.
 *
 * An empty map is returned (with NO member loop at all) when the copy uses no
 * tokens — the common case while a rep is typing prose.
 */
export function checkCopyVarGapsByMemberId(
  subject: string,
  body: string,
  members: readonly CampaignMember[],
  campaign: { name?: string; event_date?: string | null; start_date?: string | null; type?: string } | null,
  sender: SenderValue | null,
): Map<string, string[]> {
  const gapMemberIds = new Map<string, string[]>();
  const usedVars = usedTemplateVars(subject, body);
  if (usedVars.size === 0) return gapMemberIds;

  for (const member of members) {
    // Same vocabulary as interpolation — one source of truth, so a token the
    // picker offers can never be "present" here but silently unfilled there.
    const vars = buildTemplateVars(member, campaign, sender);
    for (const v of usedVars) {
      if (!(vars[v] ?? '')) {
        const ids = gapMemberIds.get(v);
        if (ids) ids.push(member.id);
        else gapMemberIds.set(v, [member.id]);
      }
    }
  }
  return gapMemberIds;
}

/**
 * Lightweight gap check for a single cell's draft copy against a set of
 * members, aggregated by DISPLAY NAME.
 *
 * U11 (E1) — rebuilt as a shape transform over
 * {@link checkCopyVarGapsByMemberId} rather than its own loop, so the two can
 * never disagree about what counts as a gap. The return shape
 * (`{variable, count, memberNames}[]`, insertion-ordered by first-seen
 * variable, `memberNames` in roster order, falling back to email then
 * `'Unknown'`) is UNCHANGED — its callers (`stepTokenGaps`, the preview
 * cards) are untouched by that unit.
 */
export function checkCopyVarGaps(
  subject: string,
  body: string,
  members: CampaignMember[],
  campaign: { name?: string; event_date?: string | null; start_date?: string | null; type?: string } | null,
  sender: SenderValue | null,
): { variable: string; count: number; memberNames: string[] }[] {
  const nameById = new Map(
    members.map((member) => [member.id, member.contact_name ?? member.contact_email ?? 'Unknown'] as const),
  );
  return Array.from(checkCopyVarGapsByMemberId(subject, body, members, campaign, sender).entries())
    .filter(([, memberIds]) => memberIds.length > 0)
    .map(([variable, memberIds]) => ({
      variable,
      count: memberIds.length,
      // `?? 'Unknown'` is unreachable (every id came from `members`) but
      // keeps this a total function rather than emitting `undefined` into a
      // `string[]` if a future caller ever passes duplicate ids.
      memberNames: memberIds.map((id) => nameById.get(id) ?? 'Unknown'),
    }));
}
